import React, { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import getConfig from "next/config";
import { useDispatch } from "react-redux";
import { Psawidsimufin } from "psawidsimulation";
import { produce } from "immer";

import { getIsDeliveryPriceIncluded } from "@lib/getIsDeliveryPriceIncluded";
import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { useRemoveQueryParameters } from "@hooks/useRemoveQueryParameters";
import { useIsLLDJourneySelected } from "@hooks/useIsLLDJourneySelected";
import { useIsLOAJourneySelected } from "@hooks/useIsLOAJourneySelected";
import { useIsVACJourneySelected } from "@hooks/useIsVACJourneySelected";
import { getIsProductionMode } from "@lib/getIsProductionMode";
import { useTranslation } from "@lib/i18n";

import { IS_SERVER } from "@shared/constants";
import { getFinanceGatewayContext } from "@shared/v2/lib/getFinanceGatewayContext";

import {
  setFinanceWidgetParametersAction,
  calculatePricesAction,
  postDealAction,
} from "@redux/actions/dealActions";

import {
  useFinanceWidgetParametersSelector,
  useActiveCarPreconfigurationSelector,
  useDealSelector,
} from "@redux/reducers/deal";

import {
  useAppConfigSelector,
  useFinanceWidgetConfigurationSelector,
} from "@redux/reducers/appConfig";
import { RESERVATION_STATUSES } from "@shared/constants";
import { CTA_SOL_TYPES } from "@shared/constants";
import { DEAL_TYPE } from "@shared/constants";
import { isIndexOrConfigPage } from "@shared/helper/isIndexOrConfigPage";
import { getCarImagePath } from "@shared/v2/lib/getCarImagePath";

const { publicRuntimeConfig } = getConfig();

const { COUNTRY } = publicRuntimeConfig;

const financeWidgetInstance = Psawidsimufin.new({
  selector: "#financial-widget",
});

const FinancialWidget = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const modalJustClosed = useRef(false);
  const containerRef = useRef(null);
  const {
    i18n: { language },
  } = useTranslation();

  const removeQueryParameters = useRemoveQueryParameters();
  const financeWidgetParameters = useFinanceWidgetParametersSelector();
  const appConfig = useAppConfigSelector();
  const { configuration } = appConfig;
  const activeCarPreconfiguration = useActiveCarPreconfigurationSelector();
  const deal = useDealSelector();
  const isLLDJourneySelected = useIsLLDJourneySelected();
  const isLOAJourneySelected = useIsLOAJourneySelected();
  const isVACJourneySelected = useIsVACJourneySelected();
  const { apiUrl, clientId } = useFinanceWidgetConfigurationSelector();
  const isProduction = getIsProductionMode();

  const isDeliveryIncluded = getIsDeliveryPriceIncluded(
    router.pathname,
    deal.lastCta
  );

  const updateFinanceWidget = async ({ parameters }) => {
    // TODO 🔨 Move to Developer toolbar
    if (!IS_SERVER && !isProduction) {
      if (window.__triggerFinanceOfferExpiredError === true) {
        parameters.duration = 50;
      }
    }

    dispatch(setFinanceWidgetParametersAction(parameters));

    if (deal.status === RESERVATION_STATUSES.RESERVATION_COMPLETED) {
      modalJustClosed.current = true;
      dispatch(
        postDealAction(
          CTA_SOL_TYPES.REFRESH_OFFER_DATA,
          DEAL_TYPE.DEAL,
          null,
          true
        )
      );
    }

    dispatch(calculatePricesAction());
  };

  const displayFinanceWidgetModal = () => {
    let isModalOpen = false;

    const interval = setInterval(function() {
      const FipsaModal = document.querySelector('[id$="fipsa-widget-modal"]');

      if (!FipsaModal && isModalOpen) {
        document.body.style.overflow = "scroll";

        removeQueryParameters(["financialWidget"]);

        clearInterval(interval);

        return;
      }

      if (FipsaModal) {
        isModalOpen = true;

        document.body.style.overflow = "hidden";
      }
    }, 100);
  };

  useEffect(() => {
    if (
      (isLLDJourneySelected || isLOAJourneySelected || isVACJourneySelected) &&
      router.query.financialWidget &&
      !modalJustClosed.current
    ) {
      const contextBase = getFinanceGatewayContext(
        COUNTRY,
        language,
        deal,
        appConfig,
        {
          isDeliveryIncluded,
          withoutPromoCode: isIndexOrConfigPage(router.pathname),
        }
      );

      const context = produce(contextBase, draft => {
        draft.context.colour = configuration.finance.context.colour;

        const { presentation } = configuration.finance;

        draft.vehicle.presentation = {
          ...presentation,
          logo: getStaticAssetURL(
            getCarImagePath(activeCarPreconfiguration.id)
          ),
          famillyLabel: activeCarPreconfiguration.label,
          longLabel: activeCarPreconfiguration.label,
          engineLabel: configuration.finance.engineLabel ?? "",
          transmissionLabel: configuration.finance.transmissionLabel ?? "",
        };

        if (financeWidgetParameters) {
          draft.parameters = financeWidgetParameters;
        }
      });

      financeWidgetInstance.render({
        context,
        environment: {
          WID_FINANCE_SIMU_BASE_URL: apiUrl,
          WID_FINANCE_SIMU_CLIENT_ID: clientId,
        },
        outputFn: output => {
          updateFinanceWidget(output);
        },
      });

      displayFinanceWidgetModal();
    } else if (modalJustClosed.current) {
      modalJustClosed.current = false;
    }
  }, [router.query, router.pathname, activeCarPreconfiguration]);

  return (
    <div className="financial-widget-container">
      <div ref={containerRef} id="financial-widget" />
    </div>
  );
};

export default FinancialWidget;
